import axios from 'axios';

/**
 * Service for QuickBooks-related API operations
 */
export const quickbooksService = {
  /**
   * Fetch data from a complete URL (useful for pagination)
   * @param {string} url - Complete URL to fetch data from
   * @returns {Promise} - API response
   */
  getByUrl: (url) => axios.get(url),

  /**
   * Get customers with optional filtering by QuickBooks connection status
   * @param {number|string} page - Page number for pagination or complete cursor URL
   * @param {Object} params - Additional query parameters
   * @param {Object} options - Additional options
   * @param {string} options.quickbooksConnectionStatus - Filter for QuickBooks connection ("connected", "unconnected", or "all")
   * @param {string} options.lastSyncAfter - Filter for customers synced after a specific date (YYYY-MM-DD)
   * @param {string} options.lastSyncBefore - Filter for customers synced before a specific date (YYYY-MM-DD)
   * @returns {Promise} - API response
   */
  getCustomers: (page = 1, params = {}, options = {}) => {
    // If page is a string and looks like a URL, use it directly
    if (typeof page === 'string' && page.startsWith('http')) {
      console.log('Making request with direct cursor URL:', page);
      return axios.get(page);
    }

    // Always use the customers endpoint
    const endpoint = '/api/v2/customers/';

    // Add query parameters if needed
    const queryParams = [];

    // If we have a cursor parameter, don't add page parameter
    // They're mutually exclusive
    if (params.cursor) {
      queryParams.push(`cursor=${params.cursor}`);
      // Remove cursor from params to avoid duplication
      const { cursor, ...restParams } = params;
      params = restParams;
    } else {
      // Only add page parameter if no cursor is provided
      queryParams.push(`page=${page}`);
    }

    // Add search parameter if present
    if (params.search) {
      queryParams.push(`search=${encodeURIComponent(params.search)}`);
    }

    // Handle QuickBooks connection status filtering
    if (options.quickbooksConnectionStatus === 'connected') {
      queryParams.push('has_quickbooks=true');
    } else if (options.quickbooksConnectionStatus === 'unconnected') {
      queryParams.push('has_quickbooks=false');
    }

    // Add last sync date filters if provided in options
    if (options.lastSyncAfter) {
      queryParams.push(`last_sync_after=${encodeURIComponent(options.lastSyncAfter)}`);
    }

    if (options.lastSyncBefore) {
      queryParams.push(`last_sync_before=${encodeURIComponent(options.lastSyncBefore)}`);
    }

    // Add any remaining additional query parameters
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        // Skip parameters we've already processed
        if (key !== 'search' && key !== 'cursor' && value !== undefined && value !== null) {
          queryParams.push(`${key}=${encodeURIComponent(value)}`);
        }
      });
    }

    // Append query parameters
    let url = endpoint;
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    console.log(`getCustomers request URL (${options.quickbooksConnectionStatus || 'all'})`, url);

    return axios.get(url)
      .then(response => {
        console.log('getCustomers response pagination:', {
          hasNext: !!response.data.next,
          hasPrevious: !!response.data.previous,
          count: response.data.count || 'no count'
        });
        return response;
      });
  },

  /**
   * Get active QuickBooks sync jobs
   * @returns {Promise} - API response with active jobs
   */
  getActiveSyncJobs: () =>
    axios.get('/api/v2/sync_jobs/active/'),

  /**
   * Get all QuickBooks sync jobs (both active and completed)
   * @param {Object} params - Query parameters for filtering and pagination
   * @returns {Promise} - API response with jobs matching the criteria
   */
  getAllSyncJobs: (params = {}) => {
    // Start with the base URL
    let url = '/api/v2/sync_jobs/';

    // Add query parameters if needed
    const queryParams = [];

    // Add all parameters
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.push(`${key}=${encodeURIComponent(value)}`);
        }
      });
    }

    // Append query parameters if any exist
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return axios.get(url);
  },

  /**
   * Unified method to start QuickBooks sync operations.
   * Handles both single customer and bulk syncs.
   *
   * @param {Object} syncParams - Sync parameters.
   * @param {string} [syncParams.customerId] - Single customer ID.
   * @param {Array<string>} [syncParams.customer_ids] - Array of customer IDs for bulk sync.
   * @param {string} syncParams.syncType - 'all', 'transactions', 'balances'.
   * @param {string} syncParams.fetchMode - 'cdc', 'full', 'range', 'none'.
   * @param {string} syncParams.importMode - 'import', 'raw_only', 'balances_only'.
   * @param {string} [syncParams.gl_start_date] - GL start date (YYYY-MM-DD).
   * @param {string} [syncParams.gl_end_date] - GL end date (YYYY-MM-DD).
   * @param {string} [syncParams.tb_start_date] - TB start date (YYYY-MM-DD).
   * @param {string} [syncParams.tb_end_date] - TB end date (YYYY-MM-DD).
   * @returns {Promise} - API response with job details.
   */
  syncQuickbooksData: (syncParams) => {
    const { customerId, ...body } = syncParams;

    // Determine endpoint based on whether customerId is provided
    const url = customerId
      ? `/api/v2/sync_quickbooks/${customerId}/`
      : '/api/v2/sync_quickbooks/';

    console.log(`[syncQuickbooksData] Posting to ${url} with body:`, body);
    return axios.post(url, body);
  },

  exportOverview: (customerIds = null) => {
    const data = customerIds ? { customer_ids: customerIds } : {};
    return axios.post('/api/v2/export_quickbooks_overview/', data);
  },

  /**
   * DEPRECATED: Use syncQuickbooksData instead.
   * Syncs all transactions for a specific customer.
   * @param {string} customerId - The ID of the customer to sync.
   * @returns {Promise} - API response.
   */
  syncCustomer: (customerId, params) => {
    console.warn("[DEPRECATED] quickbooksService.syncCustomer is deprecated. Use syncQuickbooksData instead.");
    const url = `/api/v2/sync_quickbooks/${customerId}/`;
    return axios.post(url, params);
  },

  /**
   * DEPRECATED: Use syncQuickbooksData instead.
   * Bulk syncs QuickBooks data for multiple customers.
   * @param {Array<string>} customerIds - Array of customer IDs to sync.
   * @param {Object} syncOptions - Sync options.
   * @returns {Promise} - API response.
   */
  bulkSync: (customerIds, syncOptions) => {
    console.warn("[DEPRECATED] quickbooksService.bulkSync is deprecated. Use syncQuickbooksData instead.");
    return axios.post('/api/v2/sync_quickbooks/', {
      customer_ids: customerIds,
      ...syncOptions,
    });
  },

  /**
   * DEPRECATED: Use syncQuickbooksData with fetchMode='full' and importMode='raw_only'.
   * Fetches all General Ledger transactions for a customer and stores them as raw data.
   * @param {string} customerId - The ID of the customer.
   * @returns {Promise} - API response.
   */
  syncAllTransactions: (customerId) => {
    console.warn("[DEPRECATED] quickbooksService.syncAllTransactions is deprecated. Use syncQuickbooksData instead.");
    return quickbooksService.syncQuickbooksData({
      customerId,
      syncType: 'all',
      fetchMode: 'full',
      importMode: 'raw_only',
    });
  },

  /**
   * DEPRECATED: Use syncQuickbooksData with fetchMode='none' and importMode='import'.
   * Imports raw QuickBooks data that has already been fetched.
   * @param {string} customerId - The ID of the customer.
   * @returns {Promise} - API response.
   */
  importRawData: (customerId) => {
    console.warn("[DEPRECATED] quickbooksService.importRawData is deprecated. Use syncQuickbooksData instead.");
    return quickbooksService.syncQuickbooksData({
      customerId,
      syncType: 'all',
      fetchMode: 'none',
      importMode: 'import',
    });
  },

  /**
   * DEPRECATED: Use syncQuickbooksData with fetchMode='none' and importMode='balances_only'.
   * Sets opening balances for a customer from existing transactions.
   * @param {string} customerId - The ID of the customer.
   * @returns {Promise} - API response.
   */
  setOpeningBalances: (customerId) => {
    console.warn("[DEPRECATED] quickbooksService.setOpeningBalances is deprecated. Use syncQuickbooksData instead.");
    return quickbooksService.syncQuickbooksData({
      customerId,
      syncType: 'balances',
      fetchMode: 'none',
      importMode: 'balances_only',
    });
  },

  /**
   * Get details for a specific sync job
   * @param {string} jobId - ID of the job to fetch
   * @returns {Promise} - API response with job details
   */
  getJobDetails: (jobId) =>
    axios.get(`/api/v2/sync_jobs/${jobId}/`),

  /**
   * Fetch all QuickBooks accounts for a customer
   * @param {number} customerId - ID of the customer
   * @returns {Promise} - API response
   */
  fetchAccounts: (customerId) => {
    return axios.post(`/api/v2/fetch_quickbooks_accounts/${customerId}/`);
  },

  /**
   * Get QuickBooks sync history for a customer
   * @param {number} customerId - ID of the customer
   * @param {Object} params - Additional query parameters
   * @returns {Promise} - API response with sync history
   */
  getSyncHistory: (customerId, params = {}) => {
    // Start with the base URL
    let url = `/api/v2/customers/${customerId}/quickbooks_sync/history/?`;

    // Add any additional query parameters
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }

    return axios.get(url);
  },

  /**
   * Cancel a running QuickBooks sync workflow
   * @param {string} workflowId - ID of the workflow to cancel
   * @returns {Promise} - API response
   */
  cancelSyncJob: (workflowId) => {
    return axios.patch(`/api/v2/sync_jobs/${workflowId}/`);
  },

  /**
   * Delete a QuickBooks sync workflow (removes from history)
   * ADMIN ONLY - This is a destructive operation
   * @param {string} workflowId - ID of the workflow to delete
   * @returns {Promise} - API response
   */
  deleteSyncJob: (workflowId) => {
    return axios.delete(`/api/v2/sync_jobs/${workflowId}/`);
  },

  /**
   * Bulk delete QuickBooks sync jobs (removes from history)
   * ADMIN ONLY - This is a destructive operation
   * @param {Array<string>} jobIds - Array of job IDs to delete
   * @returns {Promise} - API response
   */
  bulkDeleteSyncJobs: (jobIds) => {
    return axios.delete('/api/v2/sync_jobs/', {
      data: {
        job_ids: jobIds
      }
    });
  },

  /**
   * Get QuickBooks authorization URL for a customer
   * @param {string} customerId - ID of the customer to connect
   * @returns {Promise} - API response with auth_url
   */
  getAuthUrl: (customerId) =>
    axios.get(
      `/api/v2/client_quickbooks_accounts/${customerId}/`
    ),

  /**
   * Get details for a specific customer by ID
   * @param {string} customerId - ID of the customer to fetch
   * @returns {Promise} - API response with customer details
   */
  getCustomerById: (customerId, params = {}) =>
    axios.get(`/api/v2/customers/${customerId}/`, { params }),

  /**
   * Disconnect QuickBooks for a customer
   * @param {string|number} customerId - ID of the customer to disconnect
   * @returns {Promise} - API response
   */
  disconnectQuickbooks: (customerId) =>
    axios.post(
      '/api/v2/client_link_quickbooks/',
      {
        customer_id: customerId,
        action: 'disconnect',
      }
    ),

  /**
   * Get raw QuickBooks transaction data for a customer
   * @param {string|number} customerId - ID of the customer
   * @param {Object} params - Query parameters for pagination
   * @returns {Promise} - API response with raw transaction data
   */
  getRawTransactions: (customerId, params = {}) => {
    let url = `/api/v2/quickbooks_raw_transactions/${customerId}/`;

    // Add query parameters if needed
    const queryParams = [];
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.push(`${key}=${encodeURIComponent(value)}`);
        }
      });
    }

    // Append query parameters if any exist
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return axios.get(url);
  },

  /**
   * Force release customer sync locks (admin emergency function)
   * @param {Object} params - Query parameters
   * @param {string} params.customer_id - Optional: specific customer ID
   * @param {number} params.older_than_minutes - Optional: only release locks older than X minutes
   * @returns {Promise} - API response
   */
  forceReleaseLocks: (params = {}) => {
    const queryParams = [];
    if (params.customer_id) {
      queryParams.push(`customer_id=${encodeURIComponent(params.customer_id)}`);
    }
    if (params.older_than_minutes) {
      queryParams.push(`older_than_minutes=${encodeURIComponent(params.older_than_minutes)}`);
    }

    let url = '/api/v2/force_release_locks/';
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return axios.post(url);
  },
};
